import { useState, useCallback, useEffect } from 'react';
import * as React from 'react';
import type { AddressType, GeocodedAddress, AddressFormData } from '../../types/address';
import { useGeocoding } from '../../hooks/useGeocoding';
import { useGps } from '../../hooks/useGps';
import { AddressTypeSelector } from '../delivery/AddressTypeSelector';
import { StreetInput } from '../delivery/StreetInput';
import { NeighborhoodSelect } from '../delivery/NeighborhoodSelect';
import { GpsButton } from '../delivery/GpsButton';
import { getCSRFToken } from '../../utils/csrf';

const MapPreview = React.lazy(() =>
  import('./MapPreview').then(m => ({ default: m.MapPreview }))
);

const MapPreviewInline = React.lazy(() =>
  import('./MapPreviewInline').then(m => ({ default: m.MapPreviewInline }))
);

interface AddressFormProps {
  routes: { resolve: string; create: string };
  onSuccess: (newAddressId?: number) => void;
  hideMap?: boolean;
  initialNeighborhood?: string | null;
}

function getDisplayName(location: GeocodedAddress, data: { block?: string; house?: string; suite?: string; isCountry?: boolean }) {
  if (data.isCountry) {
    const city = location.address?.city || 'Cordoba';
    const name = location.display_name;
    if (!data.block || !data.house) return `${name}, ${city}, Argentina`;
    return `${name} Mzna. ${data.block} Lote ${data.house}, ${city}, Argentina`;
  }
  const road = location.address?.road || location.address?.pedestrian || '';
  const number = location.address?.house_number || 'S/N';
  const suite = data.suite ? ` - ${data.suite}` : '';
  const city = location.address?.city || 'Cordoba';
  return `${road} ${number}${suite}, ${city}, Argentina`;
}

export function AddressForm({ routes, onSuccess, hideMap = false, initialNeighborhood }: AddressFormProps) {
  const [addressType, setAddressType] = useState<AddressType>(initialNeighborhood ? 'country' : 'street');
  const [streetAddress, setStreetAddress] = useState('');
  const [neighborhood, setNeighborhood] = useState(initialNeighborhood || '');
  const [block, setBlock] = useState('');
  const [house, setHouse] = useState('');
  const [suite, setSuite] = useState('');
  const [notes, setNotes] = useState('');
  const [geocodedAddress, setGeocodedAddress] = useState<GeocodedAddress | null>(null);
  const [streetSuggestions, setStreetSuggestions] = useState<GeocodedAddress[]>([]);
  const [neighborhoodSuggestions, setNeighborhoodSuggestions] = useState<GeocodedAddress[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [initialNeighborhoodLoaded, setInitialNeighborhoodLoaded] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  const { searchAddress, searchNeighborhood, reverseGeocode, isLoading: isGeocodingLoading, error: geocodingError } =
    useGeocoding({ resolveUrl: routes.resolve, debounceMs: 300 });
  const { getCurrentPosition, isLoading: isGpsLoading, error: gpsError } = useGps();

  // Auto-select neighborhood when initialNeighborhood is provided
  useEffect(() => {
    if (initialNeighborhood && !initialNeighborhoodLoaded && !geocodedAddress) {
      setInitialNeighborhoodLoaded(true);
      searchNeighborhood(initialNeighborhood).then((results) => {
        if (results.length > 0) {
          // Find exact match or use first result
          const exactMatch = results.find(
            r => r.display_name.toLowerCase() === initialNeighborhood.toLowerCase()
          );
          const selected = exactMatch || results[0];
          setGeocodedAddress(selected);
          setNeighborhood(selected.display_name);
        }
      });
    }
  }, [initialNeighborhood, initialNeighborhoodLoaded, geocodedAddress, searchNeighborhood]);

  const isCountry = addressType === 'country';

  useEffect(() => {
    if (!isCountry && streetAddress.length >= 3) {
      searchAddress(streetAddress).then(setStreetSuggestions);
    } else {
      setStreetSuggestions([]);
    }
  }, [streetAddress, isCountry, searchAddress]);

  useEffect(() => {
    if (isCountry && neighborhood.length >= 2) {
      searchNeighborhood(neighborhood).then(setNeighborhoodSuggestions);
    } else {
      setNeighborhoodSuggestions([]);
    }
  }, [neighborhood, isCountry, searchNeighborhood]);

  const handleStreetAddressSelect = useCallback((address: GeocodedAddress | null) => {
    if (address?.is_private_neighborhood && address.parsed_country_data) {
      setAddressType('country');
      setNeighborhood(address.parsed_country_data.neighborhood);
      setBlock(address.parsed_country_data.block || '');
      setHouse(address.parsed_country_data.house || '');
      setStreetAddress('');
      setGeocodedAddress(address);
      setStreetSuggestions([]);
      setNeighborhoodSuggestions([]);
    } else {
      setGeocodedAddress(address);
      if (address) setStreetAddress(address.display_name);
      setStreetSuggestions([]);
    }
  }, []);

  const handleNeighborhoodSelect = useCallback((address: GeocodedAddress | null) => {
    setGeocodedAddress(address);
    if (address) setNeighborhood(address.display_name);
    setNeighborhoodSuggestions([]);
  }, []);

  const handleUseGps = async () => {
    try {
      const position = await getCurrentPosition();
      const result = await reverseGeocode(position.latitude, position.longitude);
      if (result) {
        if (result.private_neighborhood) {
          setAddressType('country');
          setGeocodedAddress({ ...result.private_neighborhood, lat: position.latitude, lon: position.longitude });
          setNeighborhood(result.private_neighborhood.display_name);
        } else {
          setAddressType('street');
          setGeocodedAddress(result);
          setStreetAddress(result.display_name);
        }
      }
    } catch {}
  };

  const handleTypeChange = (type: AddressType) => {
    setAddressType(type);
    setStreetAddress(''); setNeighborhood(''); setBlock(''); setHouse('');
    setGeocodedAddress(null); setStreetSuggestions([]); setNeighborhoodSuggestions([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!geocodedAddress) { setSubmitError('Por favor, ingresa una direccion valida.'); return; }
    if (isCountry && (!block || !house)) { setSubmitError('Por favor, ingresa la manzana y el lote.'); return; }

    setIsSubmitting(true);
    try {
      const addressName = getDisplayName(geocodedAddress, { block, house, suite, isCountry });
      const formData: AddressFormData = {
        lat: geocodedAddress.lat, lng: geocodedAddress.lon, address: addressName,
        street_number: geocodedAddress.address?.house_number || null,
        route: geocodedAddress.address?.road || null,
        locality: geocodedAddress.address?.city || null,
        administrative_area_level_1: geocodedAddress.address?.county || null,
        postal_code: geocodedAddress.address?.postcode || null,
        notes, suite, is_country: isCountry, block, house,
        neighborhood: isCountry ? neighborhood : ''
      };

      const body = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) body.set(key, String(value));
      });

      const response = await fetch(routes.create, {
        method: 'POST', body, credentials: 'include',
        headers: { 'X-XSRF-TOKEN': getCSRFToken() }
      });
      const result = await response.json();

      if (result.code === 'OK') {
        onSuccess(result.id);
      } else {
        setSubmitError(Array.isArray(result.errors) ? result.errors.join('\n') : result.error || 'Error al guardar la direccion.');
      }
    } catch {
      setSubmitError('Error de conexion. Por favor, intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = geocodedAddress !== null && (!isCountry || (block && house));

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div aria-live="polite" className="sr-only">
          {isSubmitting && 'Guardando direccion...'}
          {submitError && `Error: ${submitError}`}
        </div>

        <GpsButton onClick={handleUseGps} isLoading={isGpsLoading} error={gpsError} disabled={isSubmitting} />

        <div className="mt-2 border-t border-gray-200 pt-6">
          <div className="space-y-5">
            <AddressTypeSelector value={addressType} onChange={handleTypeChange} disabled={isSubmitting} />

            {!isCountry && (
              <>
                <StreetInput
                  value={streetAddress} onChange={setStreetAddress}
                  onAddressSelect={handleStreetAddressSelect} selectedAddress={geocodedAddress}
                  suggestions={streetSuggestions} isLoading={isGeocodingLoading}
                  error={geocodingError} disabled={isSubmitting}
                />
                <div>
                  <label htmlFor="suite" className="mb-2 block text-[15px] font-semibold text-gray-800">
                    Piso / Departamento <span className="ml-2 text-sm font-normal text-gray-400">(opcional)</span>
                  </label>
                  <input
                    id="suite" type="text" value={suite} onChange={(e) => setSuite(e.target.value)}
                    disabled={isSubmitting} autoComplete="address-line2"
                    className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-base outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    placeholder="Ej: 3ro B, Torre 2 Depto 45"
                  />
                </div>
              </>
            )}

            {isCountry && (
              <>
                <NeighborhoodSelect
                  value={neighborhood} onChange={setNeighborhood}
                  onSelect={handleNeighborhoodSelect} selectedNeighborhood={geocodedAddress}
                  suggestions={neighborhoodSuggestions} isLoading={isGeocodingLoading}
                  error={geocodingError} disabled={isSubmitting}
                />
                <div className="flex gap-4 max-sm:flex-col">
                  <div className="flex-1">
                    <label htmlFor="block" className="mb-2 block text-[15px] font-semibold text-gray-800">Manzana</label>
                    <input
                      id="block" type="text" value={block} onChange={(e) => setBlock(e.target.value)}
                      disabled={isSubmitting} maxLength={12} autoComplete="off"
                      className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-base outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                      placeholder="Ej: 5"
                    />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="house" className="mb-2 block text-[15px] font-semibold text-gray-800">Lote</label>
                    <input
                      id="house" type="text" value={house} onChange={(e) => setHouse(e.target.value)}
                      disabled={isSubmitting} maxLength={12} autoComplete="off"
                      className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-base outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                      placeholder="Ej: 12"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label htmlFor="notes" className="mb-2 block text-[15px] font-semibold text-gray-800">
                Notas para el repartidor <span className="ml-2 text-sm font-normal text-gray-400">(opcional)</span>
              </label>
              <textarea
                id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                disabled={isSubmitting} rows={3} autoComplete="off"
                className="w-full resize-none rounded-lg border-2 border-gray-300 px-4 py-3 text-base outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                placeholder="Ej: Casa blanca con rejas negras, tocar timbre"
              />
            </div>

            {/* Map - Mobile only (inline with toggle) */}
            {!hideMap && (
              <div className="lg:hidden">
                <React.Suspense fallback={<div className="h-[350px] rounded-xl bg-gray-100" />}>
                  <MapPreview
                    latitude={geocodedAddress?.lat || null}
                    longitude={geocodedAddress?.lon || null}
                    height="350px"
                  />
                </React.Suspense>
              </div>
            )}

            {/* Map button - Desktop only */}
            {!hideMap && (
              <button
                type="button"
                onClick={() => setShowMapModal(true)}
                className="hidden w-full items-center justify-center gap-2 rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-[15px] font-medium text-gray-700 transition-colors hover:border-green-500 hover:text-green-600 lg:flex"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Ver ubicacion en mapa
              </button>
            )}

            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[15px] text-red-700" role="alert">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className={`w-full rounded-lg px-6 py-3.5 text-[17px] font-semibold transition-all ${
                canSubmit && !isSubmitting
                  ? 'bg-green-500 text-white hover:bg-green-600 cursor-pointer'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2.5">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Guardando...
                </span>
              ) : (
                'Guardar direccion'
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Map Modal - Desktop */}
      {showMapModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowMapModal(false); }}
        >
          <div className="relative w-full max-w-3xl rounded-2xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Ubicacion de entrega</h3>
              <button
                type="button"
                onClick={() => setShowMapModal(false)}
                className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Cerrar"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {geocodedAddress && (
              <p className="mb-3 text-sm text-gray-500">
                {getDisplayName(geocodedAddress, { block, house, suite, isCountry })}
              </p>
            )}
            <React.Suspense fallback={<div className="h-[450px] rounded-xl bg-gray-100" />}>
              <MapPreviewInline
                latitude={geocodedAddress?.lat || null}
                longitude={geocodedAddress?.lon || null}
                height="450px"
              />
            </React.Suspense>
          </div>
        </div>
      )}
    </>
  );
}
